import { useEffect, useRef } from 'react';

/**
 * Hero background: a wallet map of compact clusters, seen two ways and split
 * by a vertical line.
 *
 *   right of the line  NORMAL VIEW   every cluster is plain blue: nothing
 *                                    tells you which group is dangerous;
 *   left of the line   TRAPMAP VIEW  exactly one cluster (C-01) is the bad
 *                                    actor: its wallets turn red, link to
 *                                    each other, money pulses out from the
 *                                    wallet that funded them, and a tight
 *                                    dashed perimeter + tag marks it.
 *
 * The line sweeps by itself (normal, reveal, danger, back). Drag its grip
 * (or the line itself) to move it by hand; hovering never moves it. After a
 * while without dragging the sweep takes over again. The layout is computed
 * once and only drifts slowly. Pauses off-screen / in hidden tabs; one still
 * split frame under prefers-reduced-motion. Data is illustrative.
 */

type RGB = [number, number, number];
const C = {
  bg: '#060709',
  brand: [0, 86, 242] as RGB,
  brand2: [24, 126, 236] as RGB,
  accent: [80, 161, 250] as RGB,
  deep: [15, 64, 140] as RGB,
  red: [255, 77, 94] as RGB,
  redDeep: [150, 24, 44] as RGB,
};
const rgba = (c: RGB, a: number) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
const clamp01 = (x: number) => (x <= 0 ? 0 : x >= 1 ? 1 : x);
const ease = (x: number) => 1 - Math.pow(1 - clamp01(x), 3);
const easeInOut = (x: number) => {
  const k = clamp01(x);
  return k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
};

/** Clusters, biggest first. Only the first one is a bad actor. */
const CLUSTERS = [
  { share: 14.2, n: 18, trap: 'BUNDLE', why: 'funded by one wallet, all bought in the launch block' },
  { share: 9.7, n: 14, trap: '', why: '' },
  { share: 6.1, n: 11, trap: '', why: '' },
  { share: 5.4, n: 9, trap: '', why: '' },
  { share: 4.6, n: 12, trap: '', why: '' },
  { share: 3.9, n: 8, trap: '', why: '' },
  { share: 3.1, n: 7, trap: '', why: '' },
  { share: 2.4, n: 6, trap: '', why: '' },
  { share: 1.8, n: 5, trap: '', why: '' },
];
const BAD = CLUSTERS[0]!;
const LOOSE = 46;

export const FIELD_STATS = {
  wallets: CLUSTERS.reduce((a, c) => a + c.n, 0) + LOOSE,
  clusters: CLUSTERS.length,
  traps: 1,
  flagged: BAD.share,
};

export interface FieldStatus {
  caught: number;
  flagged: number;
  hint: { text: string; alert: boolean; label?: string };
}

interface Cluster {
  id: number;
  share: number;
  n: number;
  trap: string;
  why: string;
  label: string;
  nodes: Node[];
  hub: Node | null;
  cx: number;
  cy: number;
  rad: number;
  ph: number;
  per: number;
}
interface Node {
  x: number;
  y: number;
  bx: number;
  by: number;
  vx: number;
  vy: number;
  phase: number;
  addr: string;
  cluster: Cluster | null;
  pct: number;
  hub: boolean;
  r: number;
}
interface Link {
  a: Node;
  b: Node;
  len: number;
  k: number;
  type: 'member' | 'fund';
  mx?: number;
  my?: number;
}

function build() {
  let seed = 0x7a4f1c;
  const rnd = () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const B58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  const addr = () => {
    let a = '';
    let b = '';
    for (let i = 0; i < 4; i++) a += B58[(rnd() * 58) | 0];
    for (let i = 0; i < 4; i++) b += B58[(rnd() * 58) | 0];
    return `${a}…${b}`;
  };
  const nodes: Node[] = [];
  const links: Link[] = [];
  const clusters: Cluster[] = CLUSTERS.map((c, i) => ({
    ...c,
    id: i,
    label: `C-${String(i + 1).padStart(2, '0')}`,
    nodes: [],
    hub: null,
    cx: 0,
    cy: 0,
    rad: 0,
    ph: rnd() * Math.PI * 2,
    per: 16 + rnd() * 8,
  }));
  const radiusFor = (pct: number) => 3 + Math.sqrt(pct) * 9;
  const mk = (o: Pick<Node, 'cluster' | 'pct' | 'hub'>): Node => {
    const n: Node = { x: 0, y: 0, bx: 0, by: 0, vx: 0, vy: 0, phase: rnd() * Math.PI * 2, addr: addr(), ...o, r: radiusFor(o.pct) };
    nodes.push(n);
    return n;
  };
  clusters.forEach((cl, i) => {
    // golden-angle spiral, turned so the bad cluster sits left of centre in open space
    const ang = i * 2.39996 + Math.PI + 0.25;
    const dist = 70 + Math.sqrt(i + 1) * 95;
    const ox = Math.cos(ang) * dist * 1.35;
    const oy = Math.sin(ang) * dist * 0.85;
    const weights = Array.from({ length: cl.n }, (_, k) => (k === 0 ? 3.2 : 0.35 + rnd()));
    const wsum = weights.reduce((a, b) => a + b, 0);
    weights.forEach((w, k) => {
      const n = mk({ cluster: cl, pct: (cl.share * w) / wsum, hub: k === 0 });
      const a = rnd() * Math.PI * 2;
      const d = k === 0 ? 0 : 30 + rnd() * 40;
      n.x = ox + Math.cos(a) * d;
      n.y = oy + Math.sin(a) * d;
      cl.nodes.push(n);
      if (k === 0) cl.hub = n;
      else links.push({ a: cl.hub!, b: n, len: 34 + cl.hub!.r + n.r * 0.5, k: 0.03, type: 'member' });
    });
  });
  const flows: { link: Link; t: number; speed: number }[] = [];
  for (const [i, j] of [
    [0, 2],
    [1, 4],
    [0, 5],
    [1, 2],
    [3, 6],
    [2, 7],
  ] as const) {
    const L: Link = { a: clusters[i]!.hub!, b: clusters[j]!.hub!, len: 190, k: 0.004, type: 'fund' };
    links.push(L);
    flows.push({ link: L, t: rnd(), speed: 0.0011 + rnd() * 0.0009 });
  }
  const looseLeft = 100 - CLUSTERS.reduce((a, c) => a + c.share, 0) - 20;
  for (let i = 0; i < LOOSE; i++) {
    const n = mk({ cluster: null, pct: Math.max(0.05, (looseLeft / LOOSE) * (0.3 + rnd() * 1.4)), hub: false });
    const a = rnd() * Math.PI * 2;
    const d = 140 + rnd() * 340;
    n.x = Math.cos(a) * d * 1.4;
    n.y = Math.sin(a) * d * 0.9;
  }

  // Run the force layout to rest once; afterwards positions only drift.
  const relax = () => {
    const n = nodes.length;
    for (let i = 0; i < n; i++) {
      const a = nodes[i]!;
      for (let j = i + 1; j < n; j++) {
        const b = nodes[j]!;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d2 = dx * dx + dy * dy;
        if (d2 > 90000) continue;
        if (d2 < 0.01) {
          dx = rnd() - 0.5;
          dy = rnd() - 0.5;
          d2 = 0.5;
        }
        const d = Math.sqrt(d2);
        const same = a.cluster && a.cluster === b.cluster;
        let f = ((same ? 18 : 55) * (1 + (a.r + b.r) * 0.06)) / d2;
        const min = a.r + b.r + 3;
        if (d < min) f += ((min - d) * 0.12) / d;
        a.vx -= dx * f;
        a.vy -= dy * f;
        b.vx += dx * f;
        b.vy += dy * f;
      }
    }
    for (const L of links) {
      const dx = L.b.x - L.a.x;
      const dy = L.b.y - L.a.y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const f = ((d - L.len) * L.k) / d;
      L.a.vx += dx * f;
      L.a.vy += dy * f;
      L.b.vx -= dx * f;
      L.b.vy -= dy * f;
    }
    for (const p of nodes) {
      const g = p.cluster ? 0.0016 : 0.0009;
      p.vx -= p.x * g * 0.9;
      p.vy -= p.y * g;
      p.vx *= 0.82;
      p.vy *= 0.82;
      p.x += p.vx;
      p.y += p.vy;
    }
  };
  for (let i = 0; i < 520; i++) relax();
  for (const p of nodes) {
    p.bx = p.x;
    p.by = p.y;
    p.vx = p.vy = 0;
  }

  // the bad actor's wallets know each other: a loop around the cluster plus
  // a few chords, shown only in the TrapMap view
  const bad = clusters[0]!;
  const hub = bad.hub!;
  const members = bad.nodes.filter((m) => m !== hub).sort((a, b) => Math.atan2(a.by - hub.by, a.bx - hub.bx) - Math.atan2(b.by - hub.by, b.bx - hub.bx));
  const ties: [Node, Node][] = [];
  members.forEach((m, i) => {
    ties.push([m, members[(i + 1) % members.length]!]);
    if (i % 3 === 0) ties.push([m, members[(i + 5) % members.length]!]);
  });
  return { nodes, links, clusters, flows, bad, members, ties };
}

/** Auto sweep, one cycle: hold normal, reveal, hold danger, back. */
const CYCLE = [
  { until: 2.4, from: 0, to: 0 },
  { until: 5.0, from: 0, to: 1 },
  { until: 10.4, from: 1, to: 1 },
  { until: 13.0, from: 1, to: 0 },
  { until: 14.2, from: 0, to: 0 },
];
const CYCLE_LEN = CYCLE[CYCLE.length - 1]!.until;
const autoK = (ts: number) => {
  const t = ts % CYCLE_LEN;
  let start = 0;
  for (const seg of CYCLE) {
    if (t <= seg.until) return seg.from + (seg.to - seg.from) * easeInOut((t - start) / (seg.until - start));
    start = seg.until;
  }
  return 0;
};
/** After a drag the line stays where it was left this long, then the sweep resumes. */
const HOLD_MS = 9000;
/** Grab distance around the line / grip, px. */
const GRAB = 18;

export function TrapField({ onStatus }: { onStatus?: (s: FieldStatus) => void }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;

  useEffect(() => {
    const host = hostRef.current!;
    const canvas = canvasRef.current!;
    const tip = tipRef.current!;
    const ctx = canvas.getContext('2d', { alpha: false })!;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const { nodes, links, clusters, flows, bad, members, ties } = build();
    const hub = bad.hub!;

    const sprite = (col: RGB, col2: RGB) => {
      const s = document.createElement('canvas');
      s.width = s.height = 128;
      const g = s.getContext('2d')!;
      const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
      grd.addColorStop(0, rgba(col, 0.55));
      grd.addColorStop(0.35, rgba(col2, 0.22));
      grd.addColorStop(1, rgba(col2, 0));
      g.fillStyle = grd;
      g.fillRect(0, 0, 128, 128);
      return s;
    };
    const glowBlue = sprite(C.accent, C.brand);
    const glowRed = sprite(C.red, C.redDeep);

    let W = 0;
    let H = 0;
    let DPR = 1;
    let S = 1;
    let CX = 0;
    let CY = 0;
    let X0 = 0;
    let X1 = 0;
    const resize = () => {
      W = host.clientWidth;
      H = host.clientHeight;
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      if (W < 860) {
        CX = W * 0.5;
        CY = H * 0.24;
        S = Math.min(W / 760, H / 1250);
        X0 = 18;
        X1 = W - 18;
      } else {
        CX = W * 0.735;
        CY = H * 0.5;
        S = Math.min(W / 1550, H / 880);
        X0 = W * 0.5;
        X1 = W - 26;
      }
      S = Math.max(S, 0.42);
    };
    const gripY = () => Math.max(70, Math.min(H - 70, W < 860 ? CY - H * 0.12 : CY - H * 0.3));

    /* ------------------------------------------------------------ pointer: hover + drag */
    const ptr = { x: -1e4, y: -1e4, wx: -1e4, wy: -1e4, inside: false };
    let lineX = -1;
    let dragging = false;
    let dragOff = 0;
    let heldUntil = -1e9;
    let heldX = 0;
    const local = (e: PointerEvent) => {
      const r = host.getBoundingClientRect();
      ptr.x = e.clientX - r.left;
      ptr.y = e.clientY - r.top;
      ptr.inside = ptr.x >= 0 && ptr.y >= 0 && ptr.x <= W && ptr.y <= H;
      ptr.wx = (ptr.x - CX) / S;
      ptr.wy = (ptr.y - CY) / S;
    };
    const nearLine = () => {
      const gy = gripY();
      const dx = Math.abs(ptr.x - lineX);
      return ptr.inside && (dx < GRAB || (dx < GRAB + 10 && Math.abs(ptr.y - gy) < 34));
    };
    /** Leftmost resting spot: just outside the bad cluster, clear of the headline. */
    const x0Live = () => (W < 860 ? X0 : Math.max(W * 0.44, Math.min(X0, CX + (bad.cx - bad.rad) * S - 16)));
    const onMove = (e: PointerEvent) => {
      local(e);
      if (dragging) {
        heldX = Math.max(x0Live(), Math.min(X1, ptr.x - dragOff));
        heldUntil = performance.now() + HOLD_MS;
        if (e.cancelable) e.preventDefault();
      }
      host.style.cursor = dragging ? 'grabbing' : nearLine() ? 'grab' : '';
    };
    const onDown = (e: PointerEvent) => {
      local(e);
      if (!nearLine() || (e.pointerType === 'mouse' && e.button !== 0)) return;
      // never steal a press meant for the hero copy (search box, buttons, links)
      const hit = document.elementFromPoint(e.clientX, e.clientY);
      if (hit && !host.contains(hit) && hit.closest('a,button,input,textarea,select,[role=button],.tm-search')) return;
      dragging = true;
      dragOff = ptr.x - lineX;
      heldX = lineX;
      heldUntil = performance.now() + HOLD_MS;
      host.dataset.dragging = '1';
      host.style.cursor = 'grabbing';
      if (e.cancelable) e.preventDefault();
    };
    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      delete host.dataset.dragging;
      heldUntil = performance.now() + HOLD_MS;
      host.style.cursor = nearLine() ? 'grab' : '';
    };
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerdown', onDown, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    /* ------------------------------------------------------------ drift */
    const place = (ts: number) => {
      for (const cl of clusters) {
        const dx = Math.sin((ts / cl.per) * Math.PI * 2 + cl.ph) * 7;
        const dy = Math.cos((ts / (cl.per * 1.3)) * Math.PI * 2 + cl.ph) * 5;
        let sx = 0;
        let sy = 0;
        for (const m of cl.nodes) {
          m.x = m.bx + dx;
          m.y = m.by + dy;
          sx += m.x;
          sy += m.y;
        }
        cl.cx = sx / cl.nodes.length;
        cl.cy = sy / cl.nodes.length;
        let rmax = 0;
        for (const m of cl.nodes) rmax = Math.max(rmax, Math.hypot(m.x - cl.cx, m.y - cl.cy) + m.r);
        cl.rad = rmax + 14;
      }
      for (const p of nodes) {
        if (p.cluster) continue;
        p.x = p.bx + Math.sin(ts * 0.21 + p.phase) * 4;
        p.y = p.by + Math.cos(ts * 0.17 + p.phase) * 3;
      }
    };

    /* ------------------------------------------------------------ the line */
    const updateLine = (ts: number, now: number) => {
      const x0 = x0Live();
      let target: number;
      if (reduced) target = CX + bad.cx * S;
      else if (dragging || now < heldUntil) target = heldX;
      else target = x0 + (X1 - x0) * autoK(ts);
      if (lineX < 0 || reduced) lineX = target;
      else lineX += (target - lineX) * (dragging ? 0.45 : 0.12);
      host.dataset.line = String(Math.round(lineX));
      host.dataset.gripY = String(Math.round(gripY()));
    };
    const inTrap = (sx: number) => sx < lineX;

    /* ------------------------------------------------------------ status */
    let revealed = false;
    let pingAt = -1;
    let lastStatus = '';
    const status = (hint: FieldStatus['hint']) => {
      const key = `${revealed}|${hint.alert}|${hint.text}`;
      if (key === lastStatus) return;
      lastStatus = key;
      statusRef.current?.({ caught: revealed ? 1 : 0, flagged: revealed ? BAD.share : 0, hint });
    };
    const describe = () => `${BAD.trap.toLowerCase()} · ${bad.nodes.length} wallets ${BAD.why}, ${BAD.share.toFixed(1)}% of supply`;
    const baseHint = (): FieldStatus['hint'] =>
      revealed ? { text: describe(), alert: true, label: `${bad.label} caught` } : { text: 'Normal view: every cluster looks the same', alert: false };

    /* ------------------------------------------------------------ tooltip */
    let hover: Node | null = null;
    let autoNode: Node | null = null;
    let autoIdx = 0;
    let autoAt = -1e9;
    const tipPos = { x: 0, y: 0, ok: false };
    let tipHtml = '';
    const pick = () => {
      hover = null;
      if (!ptr.inside || dragging) return;
      let best = Infinity;
      for (const p of nodes) {
        const d = Math.hypot(p.x - ptr.wx, p.y - ptr.wy) - p.r;
        if (d < 6 / S && d < best) {
          best = d;
          hover = p;
        }
      }
    };
    const tipHtmlFor = (p: Node, trap: boolean) => {
      const cl = p.cluster;
      let html = `<div class="t-addr">${p.addr}</div>`;
      if (!cl) {
        html += `<div class="t-row"><span>Holds</span><b>${p.pct.toFixed(2)}%</b></div>`;
        return html + `<div class="t-row"><span>Linked wallets</span><b>none</b></div>`;
      }
      html += `<div class="t-row"><span>Cluster</span><b>${cl.label} · ${cl.nodes.length} wallets</b></div>`;
      html += `<div class="t-row"><span>Wallet holds</span><b>${p.pct.toFixed(2)}%</b></div>`;
      if (cl === bad && trap) {
        html += `<div class="t-row"><span>${p === hub ? 'Funded' : 'Funded by'}</span><b>${p === hub ? `${bad.nodes.length - 1} wallets` : hub.addr}</b></div>`;
        html += `<div class="t-row"><span>Cluster supply</span><b>${cl.share.toFixed(1)}%</b></div>`;
        return html + `<div class="t-trap">FLAGGED · ${BAD.trap}</div>`;
      }
      html += `<div class="t-row"><span>Cluster supply</span><b>${cl.share.toFixed(1)}%</b></div>`;
      return html + (cl === bad ? `<div class="t-ok">No warning in a normal view</div>` : `<div class="t-ok">No trap pattern</div>`);
    };
    const tipTarget = (p: Node) => {
      const sx = CX + p.x * S;
      const sy = CY + p.y * S;
      const tw = tip.offsetWidth || 200;
      const th = tip.offsetHeight || 90;
      let x = sx + p.r * S + 46;
      const y = Math.max(16, Math.min(H - th - 12, sy - th / 2 - 46));
      if (x + tw > W - 12) x = sx - p.r * S - 46 - tw;
      return { x: Math.max(12, x), y, tw, th, sx, sy };
    };

    /* ------------------------------------------------------------ one view */
    const drawScene = (trap: boolean, ts: number) => {
      ctx.setTransform(DPR * S, 0, 0, DPR * S, DPR * CX, DPR * CY);
      const px = 1 / S;
      const fc = hover?.cluster ?? null;
      // the TrapMap view gently mutes everything that isn't the bad actor
      const mute = trap ? 0.72 : 1;
      const dim = (n: Node) => (fc ? (n.cluster === fc ? 1 : 0.5) : 1);
      const isBad = (n: Node) => trap && n.cluster === bad;

      if (trap) {
        ctx.beginPath();
        ctx.arc(bad.cx, bad.cy, bad.rad, 0, Math.PI * 2);
        ctx.fillStyle = rgba(C.red, 0.07);
        ctx.fill();
        ctx.save();
        ctx.setLineDash([4 * px, 6 * px]);
        ctx.lineDashOffset = -ts * 6 * px;
        ctx.lineWidth = 1.4 * px;
        ctx.strokeStyle = rgba(C.red, 0.6 + 0.3 * (0.5 + 0.5 * Math.sin(ts * 1.4)));
        ctx.stroke();
        ctx.restore();
        const pa = ts - pingAt;
        if (pingAt >= 0 && pa < 1.4) {
          ctx.beginPath();
          ctx.arc(bad.cx, bad.cy, bad.rad + ease(pa / 1.4) * 90 * px, 0, Math.PI * 2);
          ctx.strokeStyle = rgba(C.red, 0.55 * (1 - pa / 1.4));
          ctx.lineWidth = 1.5 * px;
          ctx.stroke();
        }
      }

      ctx.globalCompositeOperation = 'lighter';
      for (const L of links) {
        const red = L.type === 'member' && isBad(L.a);
        const a = red ? 0.55 : (L.type === 'member' ? 0.34 * Math.min(dim(L.a), dim(L.b)) : 0.22) * mute;
        ctx.strokeStyle = rgba(red ? C.red : L.type === 'member' ? C.brand2 : C.accent, a);
        ctx.lineWidth = px * (red ? 1.2 : L.type === 'member' ? 1 : 1.2);
        ctx.beginPath();
        ctx.moveTo(L.a.x, L.a.y);
        if (L.type === 'member') ctx.lineTo(L.b.x, L.b.y);
        else {
          L.mx = (L.a.x + L.b.x) / 2 - (L.b.y - L.a.y) * 0.12;
          L.my = (L.a.y + L.b.y) / 2 + (L.b.x - L.a.x) * 0.12;
          ctx.quadraticCurveTo(L.mx, L.my, L.b.x, L.b.y);
        }
        ctx.stroke();
      }
      for (const f of flows) {
        const L = f.link;
        if (L.mx === undefined || L.my === undefined) continue;
        const u = f.t;
        const iu = 1 - u;
        const x = iu * iu * L.a.x + 2 * iu * u * L.mx + u * u * L.b.x;
        const y = iu * iu * L.a.y + 2 * iu * u * L.my + u * u * L.b.y;
        const a = Math.sin(u * Math.PI) * mute;
        const s = 16 * px;
        ctx.globalAlpha = a;
        ctx.drawImage(glowBlue, x - s, y - s, s * 2, s * 2);
        ctx.globalAlpha = 1;
        ctx.fillStyle = `rgba(210,230,255,${0.9 * a})`;
        ctx.beginPath();
        ctx.arc(x, y, 1.6 * px, 0, Math.PI * 2);
        ctx.fill();
      }

      if (trap) {
        // the bad actor's wallets are tied to each other...
        ctx.strokeStyle = rgba(C.red, 0.42);
        ctx.lineWidth = px;
        ctx.beginPath();
        for (const [a, b] of ties) {
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
        }
        ctx.stroke();
        // ...and money pulses out from the wallet that funded them
        members.forEach((m, i) => {
          const u = (ts * 0.4 + i / members.length) % 1;
          const x = hub.x + (m.x - hub.x) * u;
          const y = hub.y + (m.y - hub.y) * u;
          const a = Math.sin(u * Math.PI);
          const s = 10 * px;
          ctx.globalAlpha = a;
          ctx.drawImage(glowRed, x - s, y - s, s * 2, s * 2);
          ctx.globalAlpha = 1;
          ctx.fillStyle = `rgba(255,214,218,${0.9 * a})`;
          ctx.beginPath();
          ctx.arc(x, y, 1.3 * px, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      for (const p of nodes) {
        const red = isBad(p);
        if (!p.hub && !red && p !== hover) continue;
        const k = (red ? (p.hub ? 1 : 0.6) : dim(p) * (p === hover ? 1.5 : 1) * mute) * (0.85 + 0.15 * Math.sin(ts * (red ? 2 : 0.8) + p.phase));
        const s = p.r * (p.hub ? 3.4 : 2.6);
        ctx.globalAlpha = Math.min(1, k);
        ctx.drawImage(red ? glowRed : glowBlue, p.x - s, p.y - s, s * 2, s * 2);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';

      for (const p of nodes) {
        const d = dim(p);
        const r = p.r * (p === hover ? 1.12 : 1);
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        if (isBad(p)) {
          ctx.fillStyle = rgba(C.red, p.hub ? 0.7 : 0.5);
          ctx.fill();
          ctx.lineWidth = px * (p.hub ? 1.6 : 1.1);
          ctx.strokeStyle = rgba([255, 150, 160], 0.95);
        } else if (p.cluster) {
          ctx.fillStyle = rgba(p.cluster === bad ? C.brand : C.deep, (p.hub ? 0.7 : 0.45) * d * mute);
          ctx.fill();
          ctx.lineWidth = px * (p.hub ? 1.5 : 1);
          ctx.strokeStyle = rgba(p.cluster === bad ? C.accent : C.brand2, (p.hub ? 1 : 0.85) * d * mute);
        } else {
          ctx.fillStyle = `rgba(255,255,255,${0.05 * d})`;
          ctx.fill();
          ctx.lineWidth = px;
          ctx.strokeStyle = `rgba(169,190,225,${0.4 * d * mute})`;
        }
        ctx.stroke();
        if (p.hub) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, Math.max(1.5 * px, r * 0.22), 0, Math.PI * 2);
          ctx.fillStyle = isBad(p) ? 'rgba(255,225,228,0.95)' : rgba(C.accent, 0.9 * d * mute);
          ctx.fill();
        }
      }
      if (hover) {
        const h = hover as Node;
        ctx.beginPath();
        ctx.arc(h.x, h.y, h.r * 1.12 + 5 * px, 0, Math.PI * 2);
        ctx.lineWidth = px;
        ctx.strokeStyle = 'rgba(255,255,255,.7)';
        ctx.stroke();
      }
    };

    /* ------------------------------------------------------------ frame */
    const draw = (t: number, now: number) => {
      const ts = t / 1000;
      place(ts);
      if (!reduced) for (const f of flows) f.t = (f.t + f.speed) % 1;
      updateLine(ts, now);

      const nowRevealed = inTrap(CX + bad.cx * S);
      if (nowRevealed !== revealed) {
        revealed = nowRevealed;
        if (revealed) pingAt = ts;
      }

      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.fillStyle = C.bg;
      ctx.fillRect(0, 0, W, H);

      // TrapMap view, left of the line
      if (lineX > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, lineX, H);
        ctx.clip();
        const wash = ctx.createLinearGradient(lineX - 260, 0, lineX, 0);
        wash.addColorStop(0, 'rgba(255,40,60,0)');
        wash.addColorStop(1, 'rgba(255,40,60,0.06)');
        ctx.fillStyle = wash;
        ctx.fillRect(Math.max(0, lineX - 260), 0, Math.min(260, lineX), H);
        drawScene(true, ts);
        ctx.restore();
      }
      // normal view, right of the line
      if (lineX < W) {
        ctx.save();
        ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        ctx.beginPath();
        ctx.rect(lineX, 0, W - lineX, H);
        ctx.clip();
        drawScene(false, ts);
        ctx.restore();
      }

      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      // tag on the bad cluster (TrapMap side only)
      ctx.font = `600 11px "Geist Mono", ui-monospace, monospace`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      {
        const txt = `${BAD.trap} · ${BAD.share.toFixed(1)}% · ${bad.nodes.length} WALLETS`;
        const w = ctx.measureText(txt).width + 26;
        const hgt = 21;
        let lx = CX + (bad.cx + bad.rad * 0.71) * S + 4;
        let ly = CY + (bad.cy - bad.rad * 0.71) * S - hgt - 2;
        lx = Math.max(12, Math.min(W - w - 12, lx));
        ly = Math.max(12, Math.min(H - hgt - 12, ly));
        if (lx < lineX) {
          ctx.save();
          ctx.beginPath();
          ctx.rect(0, 0, lineX, H);
          ctx.clip();
          ctx.fillStyle = 'rgba(12,6,8,0.9)';
          ctx.beginPath();
          ctx.roundRect(lx, ly, w, hgt, 5);
          ctx.fill();
          ctx.strokeStyle = rgba(C.red, 0.7);
          ctx.lineWidth = 1;
          ctx.stroke();
          const dx = lx + 10;
          const dy = ly + hgt / 2;
          ctx.fillStyle = rgba(C.red, 1);
          ctx.beginPath();
          ctx.moveTo(dx, dy - 3.5);
          ctx.lineTo(dx + 3.5, dy);
          ctx.lineTo(dx, dy + 3.5);
          ctx.lineTo(dx - 3.5, dy);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = 'rgba(255,190,197,0.98)';
          ctx.fillText(txt, lx + 18, dy + 0.5);
          ctx.restore();
        }
      }

      // the line, its grip and the two view labels
      {
        const x = Math.round(lineX) + 0.5;
        const hot = dragging || (nearLine() && !hover);
        const glow = ctx.createLinearGradient(x - 30, 0, x + 30, 0);
        glow.addColorStop(0, 'rgba(255,77,94,0)');
        glow.addColorStop(0.5, `rgba(255,77,94,${hot ? 0.16 : 0.09})`);
        glow.addColorStop(1, 'rgba(80,161,250,0)');
        ctx.fillStyle = glow;
        ctx.fillRect(x - 30, 0, 60, H);
        const lg = ctx.createLinearGradient(0, 0, 0, H);
        lg.addColorStop(0, 'rgba(255,140,150,0)');
        lg.addColorStop(0.12, 'rgba(255,170,178,0.85)');
        lg.addColorStop(0.88, 'rgba(255,170,178,0.85)');
        lg.addColorStop(1, 'rgba(255,140,150,0)');
        ctx.fillStyle = lg;
        ctx.fillRect(x - 0.75, 0, 1.5, H);
        const gy = gripY();
        const gw = hot ? 30 : 26;
        const gh = hot ? 50 : 44;
        ctx.beginPath();
        ctx.roundRect(x - gw / 2, gy - gh / 2, gw, gh, gw / 2);
        ctx.fillStyle = dragging ? 'rgba(46,12,18,0.96)' : 'rgba(14,8,11,0.92)';
        ctx.fill();
        ctx.strokeStyle = hot ? 'rgba(255,200,206,1)' : 'rgba(255,150,160,0.75)';
        ctx.lineWidth = 1.25;
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,214,218,0.95)';
        for (const s of [-1, 1]) {
          ctx.beginPath();
          ctx.moveTo(x + s * 2.5, gy - 5);
          ctx.lineTo(x + s * 7.5, gy);
          ctx.lineTo(x + s * 2.5, gy + 5);
          ctx.closePath();
          ctx.fill();
        }
        ctx.font = `600 10.5px "Geist Mono", ui-monospace, monospace`;
        ctx.textBaseline = 'middle';
        const ly = gy - gh / 2 - 16;
        const left = '◆ TRAPMAP VIEW';
        const right = 'NORMAL VIEW';
        if (x - ctx.measureText(left).width - 14 > (W < 860 ? 4 : W * 0.42)) {
          ctx.textAlign = 'right';
          ctx.fillStyle = 'rgba(255,120,132,0.95)';
          ctx.fillText(left, x - 12, ly);
        }
        if (x + ctx.measureText(right).width + 14 < W) {
          ctx.textAlign = 'left';
          ctx.fillStyle = 'rgba(170,190,220,0.8)';
          ctx.fillText(right, x + 12, ly);
        }
        ctx.textAlign = 'left';
        if (!dragging && now >= heldUntil && !reduced) {
          ctx.font = `500 10px "Geist Mono", ui-monospace, monospace`;
          ctx.textAlign = 'center';
          ctx.fillStyle = 'rgba(255,190,197,0.55)';
          ctx.fillText('DRAG', x, gy + gh / 2 + 13);
          ctx.textAlign = 'left';
        }
      }

      // tooltip: glides toward its target; the leader line follows
      const focus = hover ?? autoNode;
      if (focus) {
        const trap = inTrap(CX + focus.x * S);
        const html = tipHtmlFor(focus, trap);
        if (html !== tipHtml) {
          tip.innerHTML = html;
          tipHtml = html;
        }
        const red = trap && focus.cluster === bad;
        tip.classList.toggle('alert', red);
        const tg = tipTarget(focus);
        if (!tipPos.ok) {
          tipPos.x = tg.x;
          tipPos.y = tg.y;
          tipPos.ok = true;
        } else {
          const a = reduced ? 1 : 0.12;
          tipPos.x += (tg.x - tipPos.x) * a;
          tipPos.y += (tg.y - tipPos.y) * a;
        }
        tip.style.transform = `translate3d(${tipPos.x.toFixed(1)}px, ${tipPos.y.toFixed(1)}px, 0)`;
        tip.classList.add('on');
        const ax = tipPos.x < tg.sx ? tipPos.x + tg.tw : tipPos.x;
        const ay = Math.max(tipPos.y + 12, Math.min(tipPos.y + tg.th - 12, tg.sy));
        const ang = Math.atan2(ay - tg.sy, ax - tg.sx);
        const r0 = focus.r * S * 1.12 + 5;
        ctx.beginPath();
        ctx.moveTo(tg.sx + Math.cos(ang) * r0, tg.sy + Math.sin(ang) * r0);
        ctx.lineTo(ax, ay);
        ctx.strokeStyle = red ? rgba(C.red, 0.85) : rgba(C.accent, 0.85);
        ctx.lineWidth = 1.25;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(ax, ay, 2, 0, Math.PI * 2);
        ctx.fillStyle = red ? rgba(C.red, 0.9) : rgba(C.accent, 0.9);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(tg.sx, tg.sy, r0, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255,255,255,.7)';
        ctx.stroke();
        if (hover) {
          const cl = focus.cluster;
          if (red) status({ text: describe(), alert: true, label: bad.label });
          else if (!cl) status({ text: 'Independent holder, no linked wallets', alert: false });
          else if (cl === bad) status({ text: `${cl.label} looks like any other cluster here. Drag the line over it.`, alert: false });
          else status({ text: `${cl.label} · ${cl.nodes.length} linked wallets, ${cl.share.toFixed(1)}% · no trap pattern`, alert: false });
        } else status(baseHint());
      } else {
        tip.classList.remove('on');
        tipPos.ok = false;
        status(baseHint());
      }
    };

    // idle tour: stays on the bad cluster, so the same wallet flips from
    // "no warning" to "FLAGGED" as the line passes it
    const idle = (t: number) => {
      if (hover) {
        autoNode = null;
        return;
      }
      // phones: the copy sits over the map, so no idle tooltip there
      if (W < 860) {
        autoNode = null;
        return;
      }
      if (t - autoAt > 5200 || !autoNode) {
        autoAt = t;
        autoNode = autoIdx++ % 3 === 0 ? hub : members[(autoIdx * 5) % members.length]!;
      }
    };

    let raf = 0;
    let running = false;
    let visible = true;
    let fade = 0;
    let elapsed = 0;
    let lastNow = 0;
    const frame = (now: number) => {
      if (!running) return;
      elapsed += lastNow ? Math.min(100, now - lastNow) : 0;
      lastNow = now;
      pick();
      idle(elapsed);
      draw(elapsed, now);
      if (fade < 1) {
        fade = Math.min(1, fade + 0.025);
        canvas.style.opacity = fade.toFixed(3);
      }
      raf = requestAnimationFrame(frame);
    };
    const start = () => {
      if (running || reduced || !visible || document.hidden) return;
      running = true;
      lastNow = 0;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    resize();
    status(baseHint());
    const ro = new ResizeObserver(() => {
      resize();
      if (!running) draw(reduced ? 20_000 : elapsed, performance.now());
    });
    ro.observe(host);
    const io = new IntersectionObserver(([e]) => {
      visible = !!e?.isIntersecting;
      if (visible) start();
      else stop();
    });
    io.observe(host);
    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVis);

    if (reduced) {
      canvas.style.opacity = '1';
      autoNode = hub;
      draw(20_000, performance.now());
    } else {
      canvas.style.opacity = '0';
      start();
    }

    return () => {
      stop();
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  return (
    <div className="tf" ref={hostRef} aria-hidden="true">
      <canvas ref={canvasRef} className="tf-canvas" />
      <div className="tf-ambient" />
      <div className="tf-grid" />
      <div className="tf-grain" />
      <div className="tf-tip" ref={tipRef} role="status" aria-live="polite" />
    </div>
  );
}
